/**
 * 「📋 견적서 자동 채움」 — 매입처 견적서를 Claude 로 뽑은 답(붙여넣은 글)을 발주서 내용으로 읽는다 (v2.213.1).
 *
 * 왜 따로 두나: 전에는 붙여넣은 글이 **흠 없는 JSON 일 때만** 읽었다(JSON.parse 한 번). 그런데 실제로 붙여넣는 글은
 *   ① Claude 가 앞뒤에 설명 한두 줄을 붙이고
 *   ② 안내문이 「코드블록 없이」라고 해서, 화면에 그려진 글자를 마우스로 긁어 복사하면 마크다운이 `\"` 의 역슬래시를 먹어
 *      `2.5" HDD` 같은 인치 표시가 JSON 을 깨뜨린다(운영 발주서 #34 품목에 실제로 있다 — markdown-it 으로 재현).
 *   → 「JSON 형식 오류 — Expected ',' or '}'…」 로 멈추고, 사람은 무엇을 고쳐야 할지 모른다.
 * 그래서
 *   ⓐ 안내문은 **코드 블록 하나로** 달라고 한다(코드 상자의 [복사] 단추는 글자를 그대로 복사한다)
 *   ⓑ 글 속에서 JSON 부분만 찾아 읽고, 흔한 형식 틀림(따옴표·쉼표·줄바꿈·주석·특수 공백·잘린 끝)은 고쳐 읽거나 알아듣게 알린다
 *   ⓒ 숫자가 글자로 와도(「3,910,000원」·「2EA」) 숫자로 읽고, 못 읽으면 조용히 0·1 로 두지 않고 **표시**한다
 *   ⓓ 매입처 이름은 견적서 표기(㈜…) 대신 **우리 거래처 목록의 이름**으로 맞춘다 — #34 의 「㈜이룸에스앤앤」이
 *      [매입 연동]으로 매입 자료에 새 표기로 들어갔다(나머지 119줄은 「(주)이룸에스앤앤」 · 품목 찾기도 글자 그대로 비교라 0건).
 *
 * 검사: `npm --prefix frontend run check:quote-import`
 */

import type { PoItemInput } from './po-api';

/** Claude 에게 보낼 안내문 — ⛔「코드블록 없이」를 되살리지 말 것(위 ②) */
export const QUOTE_IMPORT_PROMPT = `첨부한 매입처 견적서를 발주서에 넣을 수 있게 아래 JSON 으로 뽑아줘.

[답하는 방식]
- \`\`\`json 코드 블록 하나로만 답해줘. 블록 앞뒤에 설명·인사말은 쓰지 마.
- qty·unitPrice 는 숫자만 (따옴표·콤마·「원」 없이 — 예: 3910000).
- unitPrice 는 VAT 별도 한 개 공급단가. 견적서에 VAT 포함 금액만 있으면 1.1 로 나눠 원 단위로 반올림하고 note 에 「VAT 포함가 환산」이라고 적어줘.
- 할인은 할인한 뒤의 단가로 넣어줘 (음수 금액 줄은 만들지 마).
- 소계·합계·부가세 줄은 넣지 마 (포털이 계산해).
- 상세 내용이 여러 줄이면 \\n 으로 이어줘. 글자 속 큰따옴표(예: 2.5" HDD)는 \\" 로 써줘.
- 견적서에 없는 글자 칸은 "" 로 둬.

{
  "recipientSupplier": "<견적서를 보낸 회사 이름>",
  "reference": "<견적서 담당자 이름·직급>",
  "projectName": "<견적서에 적힌 건명 — 없으면 빈 글자>",
  "items": [
    {
      "itemName": "<품목명>",
      "description": "<상세 내용>",
      "qty": 1,
      "unitPrice": 0,
      "note": "<비고>"
    }
  ]
}`;

/** 안내문의 보기를 그대로 붙여넣은 것인가 — 자리표시(<…>)가 그대로 있다 */
function isTemplateEcho(o: Record<string, unknown>): boolean {
  return /^<[^>]*>$/.test(oneLine(o.recipientSupplier));
}

export interface ImportedItem extends PoItemInput {
  /** 수량을 숫자로 못 읽었거나 0 이하라 1 로 둠 — 원래 글자는 qtyRaw */
  qtyUnread: boolean;
  qtyRaw: string;
  /** 단가를 숫자로 못 읽어 0 으로 둠 — 원래 글자는 priceRaw */
  priceUnread: boolean;
  priceRaw: string;
}

export interface ImportedQuote {
  recipientSupplier: string;
  reference: string;
  projectName: string;
  items: ImportedItem[];
  /** items 칸이 아예 없었다 (있는데 비었으면 false) */
  itemsMissing: boolean;
}

export type QuoteParseResult =
  | {
      ok: true;
      quote: ImportedQuote;
      /** 흠 없는 JSON 이 아니라 고쳐 읽었다 — 화면이 「견적서와 맞는지 꼭 확인」을 띄운다 */
      repaired: boolean;
      /** JSON 밖에 적힌 글(코드 상자 표시 제외) — Claude 가 단서를 거기 적었을 수 있어 보여 준다 */
      outsideText: string;
      /** 대괄호로 여러 개가 왔을 때 등 — 사람에게 알릴 말 */
      notes: string[];
    }
  | {
      ok: false;
      /** 사람에게 보일 까닭 (한국어) */
      reason: string;
      /** 몇째 줄 부근인가 (붙여넣은 글 기준 · 모르면 null) */
      line: number | null;
    };

/* ───────────────────────── 읽기 (너그러운 JSON) ───────────────────────── */

class ReadError extends Error {
  constructor(
    message: string,
    public pos: number,
  ) {
    super(message);
  }
}

interface Cursor {
  s: string;
  i: number;
}

const WS = ' \t\n\r';
const QUOTES = '"“”„';
const ENDED = '글이 중간에 끝났습니다 — 끝까지 복사됐는지 확인하세요';

function skipSpace(c: Cursor): void {
  const s = c.s;
  for (;;) {
    const ch = s[c.i];
    if (ch === undefined) return;
    if (WS.includes(ch)) {
      c.i += 1;
      continue;
    }
    // 주석(// …, /* … */) — JSON 에는 없지만 LLM 이 가끔 단다
    if (ch === '/' && s[c.i + 1] === '/') {
      const nl = s.indexOf('\n', c.i);
      c.i = nl < 0 ? s.length : nl + 1;
      continue;
    }
    if (ch === '/' && s[c.i + 1] === '*') {
      const end = s.indexOf('*/', c.i + 2);
      c.i = end < 0 ? s.length : end + 2;
      continue;
    }
    return;
  }
}

function readValue(c: Cursor): unknown {
  skipSpace(c);
  const ch = c.s[c.i];
  if (ch === undefined) throw new ReadError(ENDED, c.i);
  if (ch === '{') return readObject(c);
  if (ch === '[') return readArray(c);
  if (QUOTES.includes(ch)) return readString(c);
  if (ch === '-' || ch === '₩' || (ch >= '0' && ch <= '9')) return readNumber(c);
  const word = /^(true|false|null)/.exec(c.s.slice(c.i, c.i + 5));
  if (word) {
    c.i += word[1].length;
    return word[1] === 'true' ? true : word[1] === 'false' ? false : null;
  }
  throw new ReadError(`값이 와야 할 자리에 「${ch}」가 있습니다`, c.i);
}

function readObject(c: Cursor): Record<string, unknown> {
  c.i += 1; // {
  const obj: Record<string, unknown> = {};
  for (;;) {
    skipSpace(c);
    const ch = c.s[c.i];
    if (ch === undefined) throw new ReadError(ENDED, c.i);
    if (ch === '}') {
      c.i += 1;
      return obj;
    }
    const key = readKey(c);
    skipSpace(c);
    if (c.s[c.i] === undefined) throw new ReadError(ENDED, c.i);
    if (c.s[c.i] !== ':') throw new ReadError(`「${key}」 뒤에 쌍점(:)이 없습니다`, c.i);
    c.i += 1;
    obj[key] = readValue(c);
    skipSpace(c);
    const next = c.s[c.i];
    if (next === undefined) throw new ReadError(ENDED, c.i);
    if (next === ',') {
      c.i += 1; // 끝 쉼표(,})는 다음 바퀴에서 「}」로 끝난다
      continue;
    }
    if (next === '}') {
      c.i += 1;
      return obj;
    }
    if (QUOTES.includes(next)) continue; // 쉼표가 빠진 채 다음 칸
    throw new ReadError('쉼표(,)나 닫는 괄호(})가 와야 할 자리입니다', c.i);
  }
}

function readKey(c: Cursor): string {
  const ch = c.s[c.i]!;
  if (QUOTES.includes(ch)) return readString(c);
  const m = /^[A-Za-z_$가-힣][\w$가-힣]*/.exec(c.s.slice(c.i, c.i + 100));
  if (m) {
    c.i += m[0].length;
    return m[0];
  }
  throw new ReadError(`칸 이름이 와야 할 자리에 「${ch}」가 있습니다`, c.i);
}

function readArray(c: Cursor): unknown[] {
  c.i += 1; // [
  const arr: unknown[] = [];
  for (;;) {
    skipSpace(c);
    const ch = c.s[c.i];
    if (ch === undefined) throw new ReadError(ENDED, c.i);
    if (ch === ']') {
      c.i += 1;
      return arr;
    }
    arr.push(readValue(c));
    skipSpace(c);
    const next = c.s[c.i];
    if (next === undefined) throw new ReadError(ENDED, c.i);
    if (next === ',') {
      c.i += 1;
      continue;
    }
    if (next === ']') {
      c.i += 1;
      return arr;
    }
    if (next === '{') continue; // 쉼표가 빠진 채 다음 품목
    throw new ReadError('쉼표(,)나 닫는 괄호(])가 와야 할 자리입니다', c.i);
  }
}

const ESCAPES: Record<string, string> = { '"': '"', '\\': '\\', '/': '/', b: '\b', f: '\f', n: '\n', r: '\r', t: '\t' };

/**
 * 따옴표 뒤가 「글자의 끝」 모양인가 — 공백·탭을 건너 쉼표·쌍점·닫는 괄호·줄바꿈·주석·글 끝이 오면 끝.
 * 아니면(예: `2.5" HDD` 의 따옴표 뒤 「 H」) 역슬래시가 빠진 **글자 속 따옴표**로 본다.
 */
function closesString(s: string, j: number): boolean {
  while (s[j] === ' ' || s[j] === '\t') j += 1;
  const n = s[j];
  if (n === undefined) return true;
  if (',:}]\n\r'.includes(n)) return true;
  return n === '/' && (s[j + 1] === '/' || s[j + 1] === '*');
}

function readString(c: Cursor): string {
  const s = c.s;
  const open = s[c.i]!;
  // 둥근 따옴표로 열었으면(워드·메신저를 거쳐 온 글) 어느 따옴표로든 닫힌다 · 곧은 따옴표로 열었으면 곧은 따옴표만
  const closers = open === '"' ? '"' : QUOTES;
  c.i += 1;
  let out = '';
  for (;;) {
    const ch = s[c.i];
    if (ch === undefined) throw new ReadError(ENDED, c.i);
    if (ch === '\\') {
      const e = s[c.i + 1];
      if (e === undefined) throw new ReadError(ENDED, c.i);
      const hex = s.slice(c.i + 2, c.i + 6);
      if (e === 'u' && /^[0-9a-fA-F]{4}$/.test(hex)) {
        out += String.fromCharCode(parseInt(hex, 16));
        c.i += 6;
        continue;
      }
      out += e in ESCAPES ? ESCAPES[e] : ch + e; // 모르는 이스케이프는 글자 그대로
      c.i += 2;
      continue;
    }
    if (closers.includes(ch)) {
      c.i += 1;
      if (closesString(s, c.i)) return out;
      out += ch; // 글자 속 따옴표 (역슬래시가 빠진 것)
      continue;
    }
    if (ch !== '\r') out += ch; // 진짜 줄바꿈(터미널에서 복사)도 줄바꿈으로 받는다
    c.i += 1;
  }
}

/** 천 단위 쉼표 숫자 — 뒤가 칸의 끝일 때만(「1,234」가 숫자 목록일 수는 없는 자리) */
const NUM_GROUPED = /-?\d{1,3}(?:,\d{3})+(?:\.\d+)?(?=\s*(?:[}\]\n\r]|,\s*["“”}\]\n\r]|$))/y;
const NUM_PLAIN = /-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/y;

function readNumber(c: Cursor): number {
  if (c.s[c.i] === '₩') {
    c.i += 1;
    while (c.s[c.i] === ' ') c.i += 1;
  }
  NUM_GROUPED.lastIndex = c.i;
  let m = NUM_GROUPED.exec(c.s);
  if (!m) {
    NUM_PLAIN.lastIndex = c.i;
    m = NUM_PLAIN.exec(c.s);
  }
  if (!m) throw new ReadError('숫자를 읽지 못했습니다', c.i);
  c.i += m[0].length;
  // 숫자에 붙은 단위(원·EA·개 …)는 건너뛴다
  const unit = /[^\s,}\]]*/y;
  unit.lastIndex = c.i;
  const u = unit.exec(c.s);
  if (u) c.i += u[0].length;
  return Number(m[0].replace(/,/g, ''));
}

/** JSON 이 시작하는 자리 — 첫 「{」, 또는 「[」 바로 뒤(공백 건너)가 「{」 인 대괄호 (앞 설명의 「[참고]」 같은 글자는 건너뛴다) */
function startCandidates(s: string): number[] {
  const out: number[] = [];
  for (let i = 0; i < s.length && out.length < 8; i += 1) {
    const ch = s[i];
    if (ch === '{') out.push(i);
    else if (ch === '[') {
      let j = i + 1;
      while (s[j] !== undefined && WS.includes(s[j]!)) j += 1;
      if (s[j] === '{') out.push(i);
    }
  }
  return out;
}

function lineOf(s: string, pos: number): number {
  let line = 1;
  for (let i = 0; i < pos && i < s.length; i += 1) if (s[i] === '\n') line += 1;
  return line;
}

/* ───────────────────────── 발주서 모양으로 ───────────────────────── */

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function toText(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return v.map(toText).filter((x) => x !== '').join('\n');
  return '';
}

/** 한 줄 칸(매입처·품목명·비고 …) — 줄바꿈은 띄어쓰기로 */
function oneLine(v: unknown): string {
  return toText(v)
    .replace(/\\r\\n|\\n/g, ' ')
    .replace(/\s*\n\s*/g, ' ')
    .trim();
}

/** 여러 줄 칸(상세 내용) — 글자 그대로의 「\n」(두 번 이스케이프된 것)도 줄바꿈으로 */
function multiLine(v: unknown): string {
  return toText(v).replace(/\\r\\n|\\n/g, '\n').trim();
}

/**
 * 숫자 칸 — 숫자면 그대로, 글자면 「3,910,000원」「₩3,910,000」「2EA」「1식」「12개월」까지 읽는다.
 * 숫자가 둘 이상 섞였으면(「1~2」·「2x3」) 모호하니 못 읽음(null).
 */
export function toNumber(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v !== 'string') return null;
  const t = v.replace(/\s+/g, '').replace(/^₩/, '');
  const m = /^(-?)(\d{1,3}(?:,\d{3})+|\d{1,3}(?:\.\d{3}){2,}|\d+)(\.\d+)?(.*)$/.exec(t);
  if (!m) return null;
  const [, sign, intPart, frac = '', rest = ''] = m;
  if (/\d/.test(rest)) return null;
  const n = Number(sign + intPart.replace(/[.,]/g, '') + frac);
  return Number.isFinite(n) ? n : null;
}

function toItem(o: Record<string, unknown>): ImportedItem {
  const qtyN = toNumber(o.qty);
  const priceN = toNumber(o.unitPrice);
  const qtyUnread = qtyN === null || qtyN <= 0;
  return {
    itemName: oneLine(o.itemName),
    description: multiLine(o.description),
    qty: qtyUnread ? 1 : qtyN,
    unitPrice: priceN ?? 0,
    note: oneLine(o.note),
    qtyUnread,
    qtyRaw: qtyUnread ? toText(o.qty) : '',
    priceUnread: priceN === null,
    priceRaw: priceN === null ? toText(o.unitPrice) : '',
  };
}

/** 틀만 있고 아무것도 안 적힌 품목(안내문의 보기를 그대로 돌려준 것 등)은 뺀다 */
function isBlankItem(it: ImportedItem): boolean {
  return !it.itemName && !it.description && !it.note && it.unitPrice === 0;
}

function toQuote(o: Record<string, unknown>): ImportedQuote {
  const rawItems = o.items;
  const items = Array.isArray(rawItems)
    ? rawItems.filter(isPlainObject).map(toItem).filter((it) => !isBlankItem(it))
    : [];
  return {
    recipientSupplier: oneLine(o.recipientSupplier),
    reference: oneLine(o.reference),
    projectName: oneLine(o.projectName),
    items,
    itemsMissing: !Array.isArray(rawItems),
  };
}

/** 대괄호로 온 답 — [견적서, …] 면 첫 견적서, [품목, …] 이면 품목 목록으로 */
function pickQuote(v: unknown, notes: string[]): Record<string, unknown> | null {
  if (isPlainObject(v)) return v;
  if (!Array.isArray(v)) return null;
  const objs = v.filter(isPlainObject);
  if (objs.length === 0) return null;
  if ('items' in objs[0]! || 'recipientSupplier' in objs[0]!) {
    if (objs.length > 1) notes.push(`견적서가 ${objs.length}개 들어 있어 첫 번째만 읽었습니다`);
    return objs[0]!;
  }
  if (objs.some((x) => 'itemName' in x || 'unitPrice' in x || 'qty' in x)) return { items: objs };
  return null;
}

/** 코드 상자 표시(```json · ```)를 걷어 낸 바깥 글 */
function outsideOf(s: string, start: number, end: number): string {
  const strip = (x: string) => x.replace(/```[a-zA-Z]*/g, '').trim();
  return [strip(s.slice(0, start)), strip(s.slice(end))].filter(Boolean).join('\n').slice(0, 400);
}

/**
 * 붙여넣은 글 → 발주서 내용. 빈 글이면 null.
 * 흠 없는 JSON 이면 JSON.parse 결과를 그대로 쓰고, 아니면 너그럽게 읽는다(repaired=true).
 */
export function parseQuoteText(text: string): QuoteParseResult | null {
  const s = text
    .replace(/\r\n?/g, '\n')
    .replace(/[﻿​-‍⁠]/g, '') // 보이지 않는 글자
    .replace(/[  -   　]/g, ' '); // 특수 공백(웹에서 복사할 때 섞인다)
  if (!s.trim()) return null;

  const starts = startCandidates(s);
  if (starts.length === 0) {
    return { ok: false, reason: '중괄호 { 로 시작하는 JSON 이 없습니다 — Claude 답의 코드 상자를 복사했는지 확인하세요', line: null };
  }
  /* 시작점 후보를 차례로 읽는다 — 앞 설명 글에 「{」가 섞였거나, 안내문 보기까지 같이 붙여넣었을 수 있다.
   *   ⚠읽다 실패한 곳까지의 「{」(품목 등 안쪽 괄호)는 다시 시작점으로 삼지 않는다 — 끝이 잘린 글에서
   *     품목 하나만 떼어 「읽었다」고 하면 안 된다(「끝까지 복사됐는지」를 알려야 한다). */
  let reach = -1;
  let firstErr: ReadError | null = null;
  let sawTemplate = false;
  /** 읽히긴 했는데 발주서 칸이 없던 겉 괄호({"견적서": { … }}) — 안쪽 답의 「바깥 글」은 겉 괄호 밖만 센다 */
  const wrappers: Array<[number, number]> = [];
  for (const start of starts) {
    if (start < reach) continue;
    const c: Cursor = { s, i: start };
    let value: unknown;
    try {
      value = readValue(c);
    } catch (e) {
      if (!(e instanceof ReadError)) throw e;
      if (!firstErr) firstErr = e;
      reach = Math.max(reach, e.pos);
      continue;
    }
    const region = s.slice(start, c.i);
    let repaired = false;
    try {
      value = JSON.parse(region);
    } catch {
      repaired = true;
    }
    const notes: string[] = [];
    const obj = pickQuote(value, notes);
    if (!obj || !('items' in obj || 'recipientSupplier' in obj || 'reference' in obj || 'projectName' in obj)) {
      wrappers.push([start, c.i]);
      continue;
    }
    if (isTemplateEcho(obj)) {
      sawTemplate = true;
      reach = Math.max(reach, c.i);
      continue;
    }
    const outer = wrappers.find(([a, b]) => a <= start && c.i <= b) ?? [start, c.i];
    return { ok: true, quote: toQuote(obj), repaired, outsideText: outsideOf(s, outer[0], outer[1]), notes };
  }
  if (firstErr) return { ok: false, reason: firstErr.message, line: lineOf(s, firstErr.pos) };
  if (sawTemplate) return { ok: false, reason: '안내문의 보기(<…>)를 붙여넣었습니다 — Claude 의 답을 붙여 주세요', line: null };
  return { ok: false, reason: '발주서 내용(매입처·품목)을 찾지 못했습니다', line: null };
}

/* ───────────────────────── 매입처 이름 맞추기 ───────────────────────── */

/**
 * 회사명 정규화 — DB 함수 `norm_company` 와 같은 규칙(㈜·(주)·주식회사·유한회사 + 공백·점·괄호·중점·쉼표·따옴표·하이픈·밑줄 제거 → 소문자).
 * ⚠바꾸려면 db/…_norm_company_fn.sql 과 함께.
 */
export function normCompany(s: string): string {
  return s
    .replace(/㈜|（주）|\(주\)|주식회사|유한회사/g, '')
    .replace(/[\s.()·ㆍ・,'`_-]/g, '')
    .toLowerCase();
}

export type SupplierResolution =
  | { name: string; how: 'keep-current' | 'known' | 'as-is' }
  | { name: ''; how: 'none' };

/**
 * 견적서의 매입처 이름을 폼에 넣을 이름으로.
 *   ① 지금 폼의 매입처와 같은 회사면 **지금 이름 그대로**(복사해 온 결제조건을 지킨다 — 매입처가 「바뀌면」 다시 채우므로)
 *   ② 거래처 목록·매입 이력(known — 거래처 마스터가 앞)에 같은 회사가 있으면 **그 이름**
 *   ③ 없으면 견적서 표기 그대로
 */
export function resolveSupplier(quoteName: string, current: string, known: readonly string[]): SupplierResolution {
  const q = quoteName.trim();
  if (!q) return { name: '', how: 'none' };
  const key = normCompany(q);
  if (!key) return { name: q, how: 'as-is' };
  const cur = current.trim();
  if (cur && normCompany(cur) === key) return { name: cur, how: 'keep-current' };
  const hit = known.find((k) => k.trim() && normCompany(k) === key);
  if (hit) return { name: hit.trim(), how: 'known' };
  return { name: q, how: 'as-is' };
}
